/**
 * Audio file loader for the Flow editor.
 *
 * Single entry point used by the "Load MP3" button and by drag-and-drop.
 * Creates a blob URL for local playback and concurrently asks the backend
 * to extract BPM / first-beat offset / duration via /api/audio/analyze.
 */

import { useFlowStore } from './flowStore';
import { cacheAudioFile } from './audioCache';
import { API_BASE } from '../../api/apiBase';

export interface AudioAnalysis {
  bpm: number;
  offset_seconds: number;
  duration_seconds: number;
  filename: string;
  /** librosa's internal resampled rate (used for beat detection). */
  sample_rate: number;
  beat_count: number;
  /** Native sample rate of the source file (Hz). */
  audio_frequency?: number;
  /** Channel count (1 mono, 2 stereo). */
  audio_channels?: number;
  /** Total sample count (frames × channels). */
  audio_samples?: number;
}

export async function loadAudioFile(file: File): Promise<AudioAnalysis> {
  const store = useFlowStore.getState();
  store.setAudioLoading(true);
  store.setAudioError(null);

  const blobUrl = URL.createObjectURL(file);

  let analysis: AudioAnalysis;
  try {
    const formData = new FormData();
    formData.append('audio', file, file.name);
    const res = await fetch(`${API_BASE}/api/audio/analyze`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => `HTTP ${res.status}`);
      throw new Error(text || `Analysis failed (${res.status})`);
    }
    analysis = await res.json();
  } catch (err: any) {
    URL.revokeObjectURL(blobUrl);
    const msg = err?.message || 'Failed to analyze audio';
    store.setAudioError(msg);
    throw err;
  }

  store.setAudio({
    url: blobUrl,
    name: file.name,
    bpm: analysis.bpm,
    offsetSeconds: analysis.offset_seconds,
    durationSeconds: analysis.duration_seconds,
    audioFrequency: analysis.audio_frequency,
    audioChannels: analysis.audio_channels,
    audioSamples: analysis.audio_samples,
  });

  // Fire-and-forget: stash the raw bytes in IndexedDB so a browser refresh
  // can reconstitute the blob URL on next mount.
  cacheAudioFile(file).catch(() => { /* non-fatal */ });

  return analysis;
}
